// Time formatting utilities for consistent 12-hour AM/PM format across the site with UK timezone

/**
 * Format a date string to 12-hour time format (e.g., "2:30 PM") in UK timezone
 */
export const formatTime12Hour = (dateString: string): string => {
  return new Date(dateString).toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Europe/London'
  });
};

/**
 * Format a date string to full date and 12-hour time (e.g., "25 Dec 2023 at 2:30 PM") in UK format
 */
export const formatDateTime12Hour = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London'
  });
  const timeStr = date.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Europe/London'
  });
  return `${dateStr} at ${timeStr}`;
};

/**
 * Format a date string to short date format (e.g., "25 Dec") in UK format
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London'
  });
};

/**
 * Format a date string to datetime-local input format 
 * Consistent timezone handling without locale conversion
 */
export const formatDateTimeForInput = (dateString: string): string => {
  const date = new Date(dateString);
  
  // Extract date and time directly from the Date object to maintain consistency
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parse time string from various formats and convert to 24-hour format for backend
 */
export const parseTimeForBackend = (timeString: string): string => {
  // If it's already in ISO format, return as is
  if (timeString.includes('T') && timeString.includes(':')) {
    return new Date(timeString).toISOString();
  }
  
  // Otherwise, assume it's a datetime-local input and convert
  const date = new Date(timeString);
  return date.toISOString();
};

/**
 * Format time in HH:MM 12-hour format for availability settings
 */
export const formatTimeForAvailability = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

/**
 * Convert 12-hour time format to 24-hour format for backend storage
 */
export const convertTo24Hour = (time12h: string): string => {
  const [time, period] = time12h.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  
  let hours24 = hours;
  if (period === 'AM' && hours === 12) {
    hours24 = 0;
  } else if (period === 'PM' && hours !== 12) {
    hours24 = hours + 12;
  }
  
  return `${String(hours24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Format an hours value (package balance, used/remaining hours, lesson length
 * in decimal hours) for display WITHOUT rounding up. 45 minutes reads as
 * "0.75", not "0.8". We round to 2 decimal places only to kill floating-point
 * noise (e.g. 8.500000001), then drop trailing zeros so whole numbers stay
 * clean ("10", not "10.00").
 */
export const formatHours = (hours: number): string => {
  if (!isFinite(hours)) return '0';
  const twoDp = Math.round(hours * 100) / 100;
  return String(twoDp);
};

/**
 * Format duration in hours to a readable string (e.g., "30 min", "1.5 hours")
 */
export const formatDuration = (hours: number): string => {
  if (hours < 1) {
    const minutes = hours * 60;
    return `${minutes} min${minutes !== 1 ? '' : ''}`;
  } else if (hours === 1) {
    return '1 hour';
  } else if (hours === 1.5) {
    return '1:30';
  } else if (hours === 2.5) {
    return '2:30';
  } else {
    return `${hours} hours`;
  }
};